import { app, HostName } from '@microsoft/teams-js';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IAppContext {
  platform?: HostName;
  isLoading: boolean;
}

const initialContext: IAppContext = {
  isLoading: true,
};

const AppContext = createContext<IAppContext>(initialContext);

export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }

  return context;
};

export const AppContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [platform, setPlatform] = useState(initialContext.platform);
  const [subPageId, setSubPageId] = useState<string>();
  const [isLoading, setIsLoading] = useState(initialContext.isLoading);

  const navigation = useNavigate();

  const initializeApp = async () => {
    try {
      await app.initialize();
      const context = await app.getContext();
      setPlatform(context.app.host.name);
      setSubPageId(context.page.subPageId);
      app.notifySuccess();
      setIsLoading(false);
    } catch (e) {
      setPlatform(undefined);
      app.notifyFailure({
        reason: app.FailedReason.Other,
        message: 'Error occurred while initializing the app.',
      });
    }
  };

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    if (subPageId) {
      navigation(subPageId, { replace: true });
    }
  }, [subPageId, navigation]);

  const value = useMemo(
    () => ({
      platform,
      isLoading,
    }),
    [platform, isLoading]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
